'use client';

import {
  MyEquipmentList,
  Conditional,
  MyEquipmentVirtualScrollList
} from '@cat-ecom/pcc-components';
import {
  formatEquipmentList,
  formatSelectedEquipmentInfo,
  getFilteredList,
  sortEquipmentList
} from 'services/equipment/utils';
import { equipmentListProps } from '@cat-ecom/pcc-components/src/components/my-equipment/types';
import { useState } from 'react';
import { MyEquipmentSearchInput } from './my-equipment-search-input';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { useTranslations } from 'next-intl';
import { CatHeading } from 'components/blocks-components';
import { useQuickToast } from 'hooks/use-toast';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { SavedListDetails } from 'app/[lang]/[esite]/(core)/account/(with-nav)/saved-lists/types';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { RegisterSignInButtonToast } from '../../equipment-toast/register-signin-button-toast';
import type { SelectedEquipmentInfo as SelectedEquipmentInfoType } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import { Equipment } from 'services/equipment/types';
import { addEquipmentToList } from 'services/savedLists/actions';
import { useFeedback } from 'providers/feedback-provider';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { checkIsAlpPage, getAlpSeoUrl } from 'components/my-equipment/utils';
import { useCoreContext } from 'providers/core-provider';

export const SelectEquipmentDrawerForm = ({
  myEquipmentList,
  selectedEquipmentInfo,
  selectedEquipmentIndex,
  isLoggedIn,
  dcn,
  isVirtualScrollFeatureEnabled,
  shoppingList,
  showCurrentlyShoppingFor
}: {
  myEquipmentList: Equipment[];
  selectedEquipmentInfo: SelectedEquipmentInfoType;
  selectedEquipmentIndex: number;
  isLoggedIn: boolean;
  dcn: string | null;
  isVirtualScrollFeatureEnabled: boolean;
  shoppingList?: SavedListDetails;
  showCurrentlyShoppingFor?: boolean;
}) => {
  const t = useTranslations();
  const toast = useQuickToast();
  const params = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const { selectEquipment } = useCoreContext();
  const { isAlpSubpage } = checkIsAlpPage(params, pathname);
  const { dismissFeedback, updateFeedback } = useFeedback();
  const UNKNOWN_ERROR_MESSAGE = t('UNKNOWN_ERROR_MESSAGE');
  const { selectEquipmentDrawer, closeAllDrawers } = useMyEquipmentDrawer();
  const formattedMyEquipmentList = formatEquipmentList(myEquipmentList);
  const formattedSelectedEquipmentInfo = formatSelectedEquipmentInfo(
    selectedEquipmentInfo
  );
  const sortedMyEquipmentList: equipmentListProps[] = sortEquipmentList(
    formattedMyEquipmentList,
    selectedEquipmentIndex,
    dcn
  );
  const [searchInput, setSearchInput] = useState('');
  const [equipments, setEquipments] = useState(sortedMyEquipmentList);
  const handleEquipmentSearch = (searchTerm: string) => {
    setSearchInput(searchTerm);
    const filteredList = getFilteredList(sortedMyEquipmentList, searchTerm);
    setEquipments(filteredList as equipmentListProps[]);
  };

  const onEquipmentSelected = async (eq: equipmentListProps) => {
    try {
      dismissFeedback();
      !showCurrentlyShoppingFor
        ? await addEquipmentToList(null, {
            selectedEquipment: eq,
            savedListId: shoppingList?.shoppingListId
          })
        : await selectEquipment(eq.productId, eq.serialNumber);
      selectEquipmentDrawer.isChild
        ? closeAllDrawers()
        : selectEquipmentDrawer.closeDrawer();

      const contentGuestToast = !isLoggedIn ? (
        <RegisterSignInButtonToast
          signInText={t('SIGN_IN')}
          registerText={t('SL_REGISTER')}
          guestSaveText={t('GUEST_EQUIPMENT_SAVE')}
        />
      ) : null;
      const equipmentModelOrFamilyName = eq.model || eq.equipmentFamily;
      const listEquipmentToast = selectedEquipmentIndex
        ? t('CHANGE_EQUIP_LIST_SUCCESS', { 0: equipmentModelOrFamilyName })
        : t('EQUIP_ADDED_TO_LIST', {
            0: equipmentModelOrFamilyName
          });
      const toastMessage = !showCurrentlyShoppingFor
        ? listEquipmentToast
        : t('CURRENTLY_SHOPPING', {
            0: eq.model,
            1: capitalizeText(eq.equipmentFamily)
          });
      toast.success({
        message: toastMessage,
        content: contentGuestToast,
        delay: 5
      });
      if (eq.seoUrl && isAlpSubpage) {
        const redirectUrl = getAlpSeoUrl(eq.seoUrl, pathname, params);
        router.push(redirectUrl);
      }
    } catch (error) {
      updateFeedback(UNKNOWN_ERROR_MESSAGE);
    }
  };

  return (
    <>
      <div className="mt-3 pt-1">
        <MyEquipmentSearchInput
          myEquipmentListLength={myEquipmentList?.length}
          onSearch={handleEquipmentSearch}
        />
      </div>
      {!isEmpty(equipments) ? (
        <div className="pt-3">
          <Conditional
            test={!isVirtualScrollFeatureEnabled}
            fallback={
              <MyEquipmentVirtualScrollList
                list={equipments}
                searchTerm={searchInput}
                selectedEquipment={formattedSelectedEquipmentInfo}
                onEquipmentSelect={onEquipmentSelected}
              />
            }
          >
            <MyEquipmentList
              list={equipments}
              searchTerm={searchInput}
              selectedEquipment={formattedSelectedEquipmentInfo}
              onEqpuipmentSelect={onEquipmentSelected}
            />
          </Conditional>
        </div>
      ) : (
        <div className="mt-3">
          <CatHeading variant="label">{t('MEQ_NO_EXACT_MATCH')}</CatHeading>
        </div>
      )}
    </>
  );
};
